import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await api.auth.getCurrentUser();
          if (response.data.success) {
            setUser(response.data.user);
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('token');
          }
        } catch (error) {
          console.error('Error checking authentication:', error);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      // Support customer (email or phone) and admin (username) logins
      let payload = credentials;
      if (credentials && typeof credentials.email === 'string') {
        const value = credentials.email.trim();
        const digits = value.replace(/\D/g, '');
        if (value.includes('@')) {
          // Email login
          payload = { identifier: value.toLowerCase(), password: credentials.password };
        } else if (/^\d{10}$/.test(digits)) {
          // Phone number login
          payload = { identifier: digits, password: credentials.password };
        } else {
          // Treat as admin username (case-insensitive)
          payload = { username: value.toLowerCase(), password: credentials.password };
        }
      }

      const response = await api.auth.login(payload);
      
      if (response.data.success) {
        const { token, user } = response.data;
        
        // Store token in localStorage
        localStorage.setItem('token', token);
        
        // Update state
        setUser(user);
        setIsAuthenticated(true);
        
        return { success: true, user };
      } else {
        return { success: false, error: response.data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: api.utils.handleError(error)
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.auth.register(userData);
      
      if (response.data.success) {
        const { token, user } = response.data;
        
        // Store token in localStorage
        localStorage.setItem('token', token);
        
        // Update state
        setUser(user);
        setIsAuthenticated(true);
        
        return { success: true, user };
      } else {
        return { success: false, error: response.data.error || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: api.utils.handleError(error)
      };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint
      await api.auth.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API call success
      localStorage.removeItem('token');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateUser = async (userData) => {
    try {
      // This would be implemented when user profile update is available
      setUser(prev => ({ ...prev, ...userData }));
      return { success: true };
    } catch (error) {
      return { success: false, error: api.utils.handleError(error) };
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 